import React, { useEffect, useState } from 'react';
import { router, usePage } from '@inertiajs/react';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { formatDistanceToNow, parseISO } from 'date-fns';
import axios from 'axios';
import CreditsInfo from '../../Components/CreditsInfo';
import './IndexV2.css';

function statusChip(v) {
    const s = (v.status || '').toLowerCase();
    if (s.includes('failed')) return { label: 'failed', cls: 'failed' };
    if (s.includes('progress')) return { label: 'working…', cls: 'working' };
    if (v.built) return { label: 'built', cls: 'built' };
    return { label: 'draft', cls: 'draft' };
}

export default function IndexV2() {
    const { videos, movie_hash, movie_title, credits, user_email, flash } = usePage().props;
    const [watch, setWatch] = useState(null); // video being watched in modal

    useEffect(() => {
        if (flash?.success) toast.success(flash.success);
        if (flash?.error) toast.error(flash.error);
    }, [flash]);

    const hero = videos.find((v) => v.primary) || videos.find((v) => v.built) || null;
    const others = videos.filter((v) => hero ? v.uuid !== hero.uuid : true);

    const makePrimary = (v) => {
        router.post(`/videos/${v.uuid}/primary`, {}, { preserveScroll: true });
    };

    const destroy = async (v) => {
        if (!confirm(`Delete “${v.title}”? This can't be undone.`)) return;
        try {
            await axios.post(`/videos/${v.uuid}`, { _method: 'DELETE' });
            router.reload({ only: ['videos'] });
        } catch (e) {
            toast.error('Could not delete the video.');
        }
    };

    const uploadFile = (e) => {
        const file = e.target.files[0];
        if (!file) return;
        const formData = new FormData();
        formData.append('video_file', file);
        formData.append('movie_hash', movie_hash);
        router.post('/videos/upload', formData, { forceFormData: true });
    };

    return (
        <div className="container vx-page mt-4">
            <ToastContainer position="bottom-right" />
            <CreditsInfo movie_title={movie_title} movie_hash={movie_hash} initialCredits={credits} user_email={user_email} />

            <div className="d-flex align-items-center mt-3 mb-3 flex-wrap gap-2">
                <h3 className="mb-0 me-auto">Videos of <em className="vx-title">“{movie_title}”</em></h3>
                <a href={`/movies/${movie_hash}/videos/create`} className="btn btn-primary">+ Create your own take</a>
                <label className="btn btn-outline-secondary mb-0">
                    Upload a video
                    <input type="file" hidden accept="video/mp4,video/quicktime,video/x-msvideo,video/webm" onChange={uploadFile} />
                </label>
            </div>

            {/* ── Hero: the main video ─────────────────────────────── */}
            {hero ? (
                <div className="vx-hero">
                    <div className="vx-hero-thumb" onClick={() => hero.watch_url && setWatch(hero)}>
                        {hero.thumb
                            ? <img src={hero.thumb} alt="" />
                            : <div className="vx-thumb-empty">no poster</div>}
                        {hero.watch_url && <span className="vx-play">▶</span>}
                    </div>
                    <div className="vx-hero-body">
                        <span className="vx-primary-badge">★ Main video — shown on your script's page</span>
                        <h4 className="mb-1">{hero.title}</h4>
                        <div className="text-muted mb-2">
                            {hero.sections_count} sections · {formatDistanceToNow(parseISO(hero.created_at), { addSuffix: true })}
                        </div>
                        <div className="d-flex gap-2 flex-wrap">
                            {hero.watch_url && (
                                <button className="btn btn-primary" onClick={() => setWatch(hero)}>▶ Watch</button>
                            )}
                            {hero.sections_count > 0
                                ? <a href={`/videos/${hero.uuid}`} className="btn btn-outline-secondary">Tweak it →</a>
                                : <span className="text-muted small align-self-center">uploaded video — no editable sections</span>}
                        </div>
                    </div>
                </div>
            ) : (
                <div className="alert alert-light border">
                    No built video yet — open a draft below and build it, or create a new take.
                </div>
            )}

            {/* ── Other takes ──────────────────────────────────────── */}
            {others.length > 0 && (
                <>
                    <div className="vx-label mt-4">Other takes</div>
                    <div className="vx-grid">
                        {others.map((v) => {
                            const chip = statusChip(v);
                            return (
                                <div key={v.uuid} className="vx-card">
                                    <div className="vx-card-thumb" onClick={() => v.watch_url && setWatch(v)}>
                                        {v.thumb
                                            ? <img src={v.thumb} alt="" />
                                            : <div className="vx-thumb-empty">no poster</div>}
                                        {v.watch_url && <span className="vx-play sm">▶</span>}
                                        <span className={`vx-chip ${chip.cls}`}>{chip.label}</span>
                                    </div>
                                    <div className="vx-card-body">
                                        <div className="vx-card-title" title={v.title}>{v.title}</div>
                                        <div className="text-muted small">
                                            {v.sections_count} sections · {formatDistanceToNow(parseISO(v.created_at), { addSuffix: true })}
                                        </div>
                                        <div className="d-flex gap-2 mt-2 flex-wrap">
                                            <a href={`/videos/${v.uuid}`} className="btn btn-sm btn-outline-secondary">Edit</a>
                                            {v.built && !v.primary && (
                                                <button className="btn btn-sm btn-outline-primary" onClick={() => makePrimary(v)}
                                                    title="Show this one on your script's page instead">
                                                    ★ Make main
                                                </button>
                                            )}
                                            <button className="btn btn-sm btn-outline-danger ms-auto" onClick={() => destroy(v)}>Delete</button>
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </>
            )}

            {/* ── Watch modal ──────────────────────────────────────── */}
            {watch && (
                <div className="vx-modal-backdrop" onClick={() => setWatch(null)}>
                    <div className="vx-modal" onClick={(e) => e.stopPropagation()}>
                        <div className="d-flex align-items-center p-2 px-3 border-bottom">
                            <h6 className="mb-0">{watch.title}</h6>
                            <button className="btn-close ms-auto" onClick={() => setWatch(null)} />
                        </div>
                        <div className="p-2">
                            <video controls autoPlay src={watch.watch_url} className="w-100 rounded" />
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
